import React, { useEffect, useState } from "react";
import { Save, X, Copy, Check, ShieldCheck, AlertCircle, ExternalLink, CreditCard, PlayCircle, ChevronDown, Power } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useContentApi } from "../../context/ContentContext";

const WEBHOOK_EVENTS = [
    "Payment capture completed",
    "Payment capture denied",
    "Payment capture refunded",
    "Payment sale completed",
    "Billing subscription activated",
    "Billing subscription cancelled",
    "Billing subscription payment failed",
];

const PayPalConfigPanel = () => {
    const [cfg, setCfg] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState("");
    const [saving, setSaving] = useState(false);
    const [copied, setCopied] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);

    const webhookUrl = `${window.location.origin}/api/paypal/webhook`;

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/paypal/admin/config");
            setCfg(data);
            setDraft(data.webhook_id || "");
        } catch {
            toast.error("Could not load PayPal config");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        const value = draft.trim();
        if (!value) { toast.error("Webhook ID is required"); return; }
        setSaving(true);
        try {
            await api.put("/paypal/admin/config/webhook-id", { webhook_id: value });
            toast.success("Webhook ID saved");
            setEditing(false);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const clear = async () => {
        if (!window.confirm("Remove the saved webhook ID? Until you set a new one, incoming PayPal webhooks will not be cryptographically verified.")) return;
        try {
            await api.delete("/paypal/admin/config/webhook-id");
            toast.success("Cleared");
            await load();
        } catch { toast.error("Delete failed"); }
    };

    const copyUrl = () => {
        navigator.clipboard?.writeText(webhookUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
    };

    const setEnv = async (env) => {
        try {
            await api.put("/paypal/admin/config/env", { env });
            toast.success(`Switched to ${env}`);
            setTestResult(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to switch env");
        }
    };

    const saveClientId = async (value) => {
        await api.put("/paypal/admin/config/client-id", { client_id: value });
        toast.success("Client ID saved");
        setTestResult(null);
        await load();
    };

    const saveClientSecret = async (value) => {
        await api.put("/paypal/admin/config/client-secret", { client_secret: value });
        toast.success("Client Secret saved");
        setTestResult(null);
        await load();
    };

    const clearCreds = async () => {
        if (!window.confirm("Remove the DB-stored Client ID + Secret? The backend will fall back to whatever is in /var/www/reddog/backend/.env.")) return;
        try {
            await api.delete("/paypal/admin/config/credentials");
            toast.success("Reverted to .env credentials");
            setTestResult(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to clear");
        }
    };

    const testConn = async () => {
        setTesting(true);
        try {
            const { data } = await api.post("/paypal/admin/config/test");
            setTestResult(data);
            if (data.ok) toast.success(`PayPal OAuth ok (${data.env})`);
            else toast.error(`Connection failed: ${data.detail}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Test failed");
        } finally {
            setTesting(false);
        }
    };

    if (loading) return <p className="text-sm text-[#4A5568]">Loading…</p>;
    if (!cfg) {
        return (
            <div className="bg-white border border-[#E53935]/20 rounded-2xl p-6 max-w-xl" data-testid="paypal-config-unavailable">
                <h2 className="font-heading text-xl text-[#1C252E] mb-2">PayPal config unavailable</h2>
                <p className="text-sm text-[#4A5568] mb-4">
                    The backend did not return PayPal configuration. If you just deployed an update, the backend service may still be restarting — wait a few seconds and click retry. Otherwise check that the backend container/service is running and that <code>backend/.env</code> has the PayPal client variables set.
                </p>
                <button
                    type="button"
                    onClick={load}
                    data-testid="paypal-config-retry-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-[#1C252E] text-white hover:bg-black"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="paypal-config-panel">
            <div>
                <h2 className="font-heading text-2xl text-[#1C252E] tracking-tight">PayPal configuration</h2>
                <p className="text-sm text-[#4A5568] mt-1 max-w-2xl">Manage every PayPal credential directly from this panel — environment, Client ID, Secret, and Webhook ID. Values entered here are stored in the database and override anything in <code>backend/.env</code>.</p>
            </div>

            {/* Kill-switch — disables Donate/Shop/Raffle PayPal buttons site-wide
                and shows donors a friendly "temporarily offline" banner with
                editable title + body copy. */}
            <PaymentsKillSwitchBlock />

            {/* Environment toggle (sandbox <-> live) */}
            <div className="bg-white border border-black/10 rounded-2xl p-5 space-y-3" data-testid="paypal-env-block">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <p className="font-medium text-[#1C252E]">Environment</p>
                    <span className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568]">source: {cfg.env_source}</span>
                </div>
                <div className="flex items-center gap-2">
                    {["sandbox", "live"].map((opt) => (
                        <button
                            key={opt}
                            type="button"
                            onClick={() => setEnv(opt)}
                            data-testid={`paypal-env-${opt}-btn`}
                            className={`px-4 py-2 rounded-full text-sm font-medium border ${cfg.env === opt ? (opt === "live" ? "bg-[#E53935] text-white border-[#E53935]" : "bg-amber-100 text-amber-900 border-amber-300") : "bg-white text-[#4A5568] border-black/10 hover:border-[#1C252E]"}`}
                        >
                            {opt === "live" ? "🔴 Live" : "🧪 Sandbox"}
                        </button>
                    ))}
                    <button
                        type="button"
                        onClick={testConn}
                        disabled={testing}
                        data-testid="paypal-test-conn-btn"
                        className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-[#1C252E] text-white hover:bg-black disabled:opacity-60"
                    >
                        <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
                        {testing ? "Testing…" : "Test connection"}
                    </button>
                </div>
                {testResult && (
                    <div className={`text-xs rounded-lg p-3 ${testResult.ok ? "bg-green-50 text-green-800" : "bg-red-50 text-[#E53935]"}`} data-testid="paypal-test-result">
                        {testResult.ok
                            ? `✅ OAuth succeeded against ${testResult.env} (token prefix ${testResult.token_prefix})`
                            : `❌ ${testResult.detail || "Connection failed"}`}
                    </div>
                )}
            </div>

            {/* Credentials — editable */}
            <div className="bg-white border border-black/10 rounded-2xl p-5 space-y-4" data-testid="paypal-credentials-block">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <p className="font-medium text-[#1C252E]">PayPal API credentials</p>
                    {(cfg.client_id_source === "db" || cfg.secret_source === "db") && (
                        <button
                            type="button"
                            onClick={clearCreds}
                            data-testid="paypal-clear-creds-btn"
                            className="text-xs text-[#4A5568] hover:text-[#E53935] inline-flex items-center gap-1"
                        >
                            <X className="w-3 h-3" strokeWidth={2} /> Revert to .env
                        </button>
                    )}
                </div>

                {/* Client ID */}
                <CredentialRow
                    label="Client ID"
                    testidPrefix="paypal-client-id"
                    currentDisplay={cfg.client_id_set ? `…${cfg.client_id_last8}` : ""}
                    source={cfg.client_id_source}
                    onSave={saveClientId}
                    placeholder="AXxxxxx…live client id from developer.paypal.com"
                />

                {/* Client Secret */}
                <CredentialRow
                    label="Client Secret"
                    testidPrefix="paypal-client-secret"
                    currentDisplay={cfg.secret_set ? "•••••••• configured" : ""}
                    source={cfg.secret_source}
                    onSave={saveClientSecret}
                    placeholder="ELxxxxx…paste your live secret"
                    isSecret
                />
            </div>

            {/* Current status (read-only summary) */}
            <div className="bg-white border border-black/10 rounded-2xl p-5 space-y-3" data-testid="paypal-config-status">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <p className="font-medium text-[#1C252E] flex items-center gap-2"><CreditCard className="w-4 h-4 text-[#E53935]" strokeWidth={1.5} /> Status</p>
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${cfg.env === "live" ? "bg-red-100 text-[#E53935]" : "bg-amber-50 text-amber-800"}`}>{cfg.env}</span>
                </div>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                        <dt className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Client ID</dt>
                        <dd className="text-[#1C252E] mt-1 font-mono text-xs">
                            {cfg.client_id_set ? `…${cfg.client_id_last8}` : <span className="text-[#E53935]">Not set</span>}
                        </dd>
                    </div>
                    <div>
                        <dt className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Client Secret</dt>
                        <dd className="mt-1">{cfg.secret_set ? <span className="inline-flex items-center gap-1 text-green-700 text-xs"><Check className="w-3 h-3" strokeWidth={2.5} /> Configured</span> : <span className="text-[#E53935] text-xs">Not set</span>}</dd>
                    </div>
                    <div className="sm:col-span-2">
                        <dt className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Webhook URL (paste this into PayPal)</dt>
                        <dd className="mt-1 flex items-center gap-2">
                            <code className="flex-1 text-xs bg-[#F4F1EA] px-3 py-2 rounded-lg break-all" data-testid="webhook-url-display">{webhookUrl}</code>
                            <button onClick={copyUrl} data-testid="webhook-url-copy-btn" className="p-2 rounded-full bg-white border border-black/10 hover:bg-[#F4F1EA] text-[#4A5568]" aria-label="Copy URL">
                                {copied ? <Check className="w-4 h-4 text-green-700" strokeWidth={2} /> : <Copy className="w-4 h-4" strokeWidth={1.5} />}
                            </button>
                        </dd>
                    </div>
                </dl>
            </div>

            {/* Webhook ID — editable */}
            <div className="bg-white border border-black/10 rounded-2xl p-5 space-y-3" data-testid="paypal-webhook-block">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <p className="font-medium text-[#1C252E]">Webhook ID</p>
                    {cfg.webhook_id ? (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-green-100 text-green-800 px-2 py-0.5 rounded-full"><ShieldCheck className="w-3 h-3" strokeWidth={2.5} /> Signature verification active</span>
                    ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-amber-50 text-amber-800 px-2 py-0.5 rounded-full"><AlertCircle className="w-3 h-3" strokeWidth={2.5} /> Not configured — webhooks won&apos;t verify</span>
                    )}
                </div>

                {!editing ? (
                    <div className="flex items-center gap-3 flex-wrap">
                        <code className="flex-1 text-sm bg-[#F4F1EA] px-3 py-2 rounded-lg font-mono break-all min-w-0" data-testid="webhook-id-display">
                            {cfg.webhook_id || <span className="text-[#4A5568] italic">— none —</span>}
                        </code>
                        <button onClick={() => { setEditing(true); setDraft(cfg.webhook_id || ""); }} data-testid="webhook-id-edit-btn" className="inline-flex items-center gap-1 text-sm bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-4 py-1.5 font-medium">
                            {cfg.webhook_id ? "Change" : "Add"}
                        </button>
                        {cfg.webhook_id && (
                            <button onClick={clear} data-testid="webhook-id-clear-btn" className="text-sm text-[#4A5568] hover:text-[#E53935] inline-flex items-center gap-1"><X className="w-4 h-4" strokeWidth={2} /> Clear</button>
                        )}
                    </div>
                ) : (
                    <div className="space-y-2">
                        <input
                            data-testid="webhook-id-input"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            className="w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white font-mono focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]"
                            placeholder="8PT9YE4G3M7XK"
                        />
                        <div className="flex justify-end gap-2">
                            <button onClick={() => { setEditing(false); setDraft(cfg.webhook_id || ""); }} className="text-sm text-[#4A5568] hover:text-[#1C252E] inline-flex items-center gap-1"><X className="w-4 h-4" strokeWidth={2} /> Cancel</button>
                            <button onClick={save} disabled={saving} data-testid="webhook-id-save-btn" className="inline-flex items-center gap-1 bg-[#E53935] hover:bg-[#D32F2F] disabled:opacity-60 text-white rounded-full px-4 py-1.5 text-sm font-medium"><Save className="w-4 h-4" strokeWidth={2} /> {saving ? "Saving…" : "Save"}</button>
                        </div>
                    </div>
                )}
            </div>

            {/* Simulate webhook — admin dev tool. Runs a synthesized event
                through the same handler a signed webhook would invoke, so
                admins can confirm capture+webhook reconciliation without
                waiting for a real PayPal delivery. */}
            <SimulateWebhookBlock />

            {/* Setup instructions */}
            <details className="bg-[#F4F1EA]/60 border border-[#E53935]/10 rounded-2xl p-4 text-sm" data-testid="paypal-setup-help">
                <summary className="cursor-pointer text-[#1C252E] font-medium">How to generate the Webhook ID at PayPal</summary>
                <ol className="mt-3 space-y-2 text-[#4A5568] list-decimal list-inside">
                    <li>Open <a href={cfg.env === "live" ? "https://developer.paypal.com/dashboard/applications/live" : "https://developer.paypal.com/dashboard/applications/sandbox"} target="_blank" rel="noopener noreferrer" className="text-[#E53935] hover:underline inline-flex items-center gap-1">developer.paypal.com → Apps & Credentials <ExternalLink className="w-3 h-3" strokeWidth={2} /></a></li>
                    <li>Open your application, scroll to <strong>Webhooks</strong>, click <strong>Add Webhook</strong></li>
                    <li>Paste this URL: <code className="text-xs bg-white px-1.5 py-0.5 rounded">{webhookUrl}</code></li>
                    <li>Tick these event types:
                        <ul className="list-disc list-inside ml-3 mt-1 text-xs">
                            {WEBHOOK_EVENTS.map((e) => <li key={e}>{e}</li>)}
                        </ul>
                    </li>
                    <li>Save → copy the <strong>Webhook ID</strong> (looks like <code>8PT9YE4G3M7XK</code>) → paste it in the field above and click <strong>Save</strong></li>
                </ol>
            </details>
        </div>
    );
};

export default PayPalConfigPanel;

// Payments kill-switch — admin can disable PayPal/Venmo across the whole site
// without removing credentials. Useful during a PayPal account hold, banking
// outage, or any time you want to gracefully take payments offline. Shows
// donors the editable title + body instead of the broken buttons.
const PaymentsKillSwitchBlock = () => {
    const { c, saveContent, loaded } = useContentApi();
    // PayPal-specific kill-switch. Independent of Stripe. The master
    // `payments.disabled` (in System settings) overrides both at once.
    const enabled = c("paypal.disabled", "false") === "true";
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [editingCopy, setEditingCopy] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (loaded) {
            setTitle(c("payments.disabled_title", "Online donations temporarily offline"));
            setBody(c("payments.disabled_body", ""));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loaded]);

    const toggle = async () => {
        setSaving(true);
        try {
            await saveContent("paypal.disabled", enabled ? "false" : "true");
            toast.success(enabled ? "PayPal payments re-enabled" : "PayPal payments DISABLED (Stripe still active)");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save");
        } finally {
            setSaving(false);
        }
    };

    const saveCopy = async () => {
        setSaving(true);
        try {
            await saveContent("payments.disabled_title", title);
            await saveContent("payments.disabled_body", body);
            setEditingCopy(false);
            toast.success("Message saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save copy");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            data-testid="payments-killswitch-block"
            className={`border rounded-2xl p-5 space-y-4 ${enabled ? "bg-amber-50 border-amber-300" : "bg-white border-black/10"}`}
        >
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-3">
                    <Power className={`w-5 h-5 mt-0.5 flex-shrink-0 ${enabled ? "text-amber-700" : "text-[#E53935]"}`} strokeWidth={2} />
                    <div>
                        <p className="font-medium text-[#1C252E]">Disable PayPal & Venmo only</p>
                        <p className="text-xs text-[#4A5568] mt-1 max-w-2xl leading-relaxed">
                            Hides only the PayPal + Venmo buttons across the site (Donate, Shop, Raffle). <strong>Stripe stays live and donors can still pay with card.</strong> Flip this DURING a PayPal account hold, banking outage, or when PayPal returns <code>PAYEE_ACCOUNT_RESTRICTED</code>.
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={toggle}
                    disabled={saving || !loaded}
                    data-testid="payments-killswitch-toggle"
                    className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors flex-shrink-0 ${enabled ? "bg-amber-500" : "bg-[#E53935]"}`}
                >
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${enabled ? "translate-x-8" : "translate-x-1"}`} />
                </button>
            </div>

            {enabled && (
                <div className="bg-white border border-amber-200 rounded-xl p-4" data-testid="payments-killswitch-preview">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-amber-800 mb-2">Donors currently see</p>
                    {!editingCopy ? (
                        <>
                            <p className="font-bold text-amber-900 text-sm mb-1">{title}</p>
                            <p className="text-sm text-amber-900 leading-relaxed whitespace-pre-wrap">{body}</p>
                            <button
                                type="button"
                                onClick={() => setEditingCopy(true)}
                                data-testid="payments-killswitch-edit-btn"
                                className="mt-3 text-xs text-amber-800 underline hover:text-amber-900"
                            >
                                Edit message
                            </button>
                        </>
                    ) : (
                        <div className="space-y-2">
                            <label className="block text-[10px] uppercase tracking-wider font-bold text-[#4A5568]">Title</label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                data-testid="payments-killswitch-title-input"
                                className="w-full border border-black/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]/40"
                            />
                            <label className="block text-[10px] uppercase tracking-wider font-bold text-[#4A5568] mt-2">Body</label>
                            <textarea
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                rows={4}
                                data-testid="payments-killswitch-body-input"
                                className="w-full border border-black/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]/40"
                            />
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={saveCopy}
                                    disabled={saving}
                                    data-testid="payments-killswitch-save-btn"
                                    className="px-3 py-1.5 rounded-full text-xs font-medium bg-[#E53935] hover:bg-[#D32F2F] text-white disabled:opacity-60"
                                >
                                    {saving ? "Saving…" : "Save"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setEditingCopy(false)}
                                    className="px-3 py-1.5 rounded-full text-xs font-medium border border-black/10 text-[#4A5568] hover:border-[#1C252E]"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Simulate webhook — admin dev tool. Picks an existing PayPal order, lets the
// admin pick an event type, and POSTs to /api/paypal/admin/simulate-webhook
// which runs the same `_handle_webhook_event` pipeline as a signed webhook.
// Used to confirm the capture+webhook `verified` reconciliation works
// end-to-end without waiting for PayPal to actually fire a webhook.
const SimulateWebhookBlock = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [orderId, setOrderId] = useState("");
    const [eventType, setEventType] = useState("PAYMENT.CAPTURE.COMPLETED");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const loadOrders = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/paypal/admin/orders/recent?limit=25");
            setOrders(data || []);
            if (!orderId && data && data.length) setOrderId(data[0].id);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not load recent orders");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { loadOrders(); }, []);

    const run = async () => {
        if (!orderId) { toast.error("Pick an order first"); return; }
        setBusy(true);
        setResult(null);
        try {
            const { data } = await api.post("/paypal/admin/simulate-webhook", {
                order_id: orderId,
                event_type: eventType,
            });
            setResult(data);
            const verified = data?.donation_after?.verified;
            toast.success(
                verified
                    ? `Webhook simulated · donation verified (both channels agree)`
                    : `Webhook simulated · ${data?.event_type}`
            );
        } catch (e) {
            const msg = e?.response?.data?.detail || "Simulate failed";
            setResult({ ok: false, error: msg });
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="bg-white border border-black/10 rounded-2xl p-5 space-y-4" data-testid="paypal-simulate-block">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <p className="font-medium text-[#1C252E] flex items-center gap-2">
                        <PlayCircle className="w-4 h-4 text-[#E53935]" strokeWidth={1.5} /> Simulate webhook
                    </p>
                    <p className="text-xs text-[#4A5568] mt-1 max-w-2xl">
                        Synthesize a PayPal webhook event for an existing order and run it through the same handler a signed webhook would invoke. Useful for confirming the capture + webhook reconciliation flips the donation&apos;s <strong>Verified</strong> badge to ON without waiting for PayPal to deliver a real webhook.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={loadOrders}
                    disabled={loading}
                    data-testid="paypal-simulate-reload-btn"
                    className="text-xs text-[#4A5568] hover:text-[#1C252E] underline disabled:opacity-50"
                >
                    {loading ? "Loading…" : "Reload orders"}
                </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3 items-end">
                <div>
                    <label className="block text-[10px] uppercase tracking-wider font-bold text-[#4A5568] mb-1">PayPal order</label>
                    <div className="relative">
                        <select
                            value={orderId}
                            onChange={(e) => { setOrderId(e.target.value); setResult(null); }}
                            disabled={loading || orders.length === 0}
                            data-testid="paypal-simulate-order-select"
                            className="w-full appearance-none rounded-xl border border-black/10 px-4 py-2.5 pr-9 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] disabled:opacity-60"
                        >
                            {orders.length === 0 ? (
                                <option value="">No recent PayPal orders</option>
                            ) : orders.map((o) => (
                                <option key={o.id} value={o.id}>
                                    {o.purpose} · ${Number(o.amount_usd || 0).toFixed(2)} · {o.status || "—"} · {o.id.slice(-10)}
                                </option>
                            ))}
                        </select>
                        <ChevronDown className="w-4 h-4 text-[#4A5568] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={1.5} />
                    </div>
                </div>
                <div>
                    <label className="block text-[10px] uppercase tracking-wider font-bold text-[#4A5568] mb-1">Event type</label>
                    <div className="relative">
                        <select
                            value={eventType}
                            onChange={(e) => setEventType(e.target.value)}
                            data-testid="paypal-simulate-event-select"
                            className="appearance-none rounded-xl border border-black/10 px-4 py-2.5 pr-9 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]"
                        >
                            <option value="PAYMENT.CAPTURE.COMPLETED">PAYMENT.CAPTURE.COMPLETED</option>
                            <option value="PAYMENT.CAPTURE.DENIED">PAYMENT.CAPTURE.DENIED</option>
                            <option value="PAYMENT.CAPTURE.REFUNDED">PAYMENT.CAPTURE.REFUNDED</option>
                        </select>
                        <ChevronDown className="w-4 h-4 text-[#4A5568] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={1.5} />
                    </div>
                </div>
                <button
                    type="button"
                    onClick={run}
                    disabled={busy || !orderId}
                    data-testid="paypal-simulate-run-btn"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium bg-[#E53935] hover:bg-[#D32F2F] text-white disabled:opacity-60"
                >
                    <PlayCircle className="w-4 h-4" strokeWidth={1.5} />
                    {busy ? "Running…" : "Simulate"}
                </button>
            </div>

            {result && (
                <div
                    data-testid="paypal-simulate-result"
                    className={`text-xs rounded-lg p-3 ${result.ok ? "bg-green-50 text-green-900" : "bg-red-50 text-[#E53935]"}`}
                >
                    {result.ok ? (
                        <>
                            <p className="font-bold mb-1">✅ Event dispatched · {result.event_type}</p>
                            <p>Event id: <code className="font-mono">{result.event_id}</code></p>
                            {result.donation_after && (
                                <p className="mt-1">
                                    Donation <code className="font-mono">{result.donation_after.id?.slice(-8)}</code>{" "}
                                    {result.donation_after.verified ? (
                                        <span className="font-bold text-green-700">is now Verified</span>
                                    ) : (
                                        <span>confirmed by [{(result.donation_after.confirmed_by || []).join(", ") || "—"}]</span>
                                    )}
                                </p>
                            )}
                            {!result.donation_after && result.order_after && (
                                <p className="mt-1">Order status: <code className="font-mono">{result.order_after.status}</code></p>
                            )}
                        </>
                    ) : (
                        <>❌ {result.error}</>
                    )}
                </div>
            )}
        </div>
    );
};

// Reusable inline editor for Client ID / Secret. Hides current value (or
// masks it as a secret), exposes Save with built-in validation.
const CredentialRow = ({ label, testidPrefix, currentDisplay, source, onSave, placeholder, isSecret }) => {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        const v = value.trim();
        if (v.length < 10) { toast.error(`${label} looks too short`); return; }
        setBusy(true);
        try { await onSave(v); setEditing(false); setValue(""); }
        catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
        finally { setBusy(false); }
    };
    const sourceTag = (
        <span className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568]">
            source: {source}
        </span>
    );
    return (
        <div className="space-y-2" data-testid={`${testidPrefix}-row`}>
            <div className="flex items-center justify-between gap-2">
                <p className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">{label}</p>
                {sourceTag}
            </div>
            {!editing ? (
                <div className="flex items-center gap-3 flex-wrap">
                    <code className="flex-1 text-sm bg-[#F4F1EA] px-3 py-2 rounded-lg font-mono break-all min-w-0" data-testid={`${testidPrefix}-display`}>
                        {currentDisplay || <span className="text-[#E53935] italic">— not set —</span>}
                    </code>
                    <button
                        type="button"
                        onClick={() => { setEditing(true); setValue(""); }}
                        data-testid={`${testidPrefix}-edit-btn`}
                        className="inline-flex items-center gap-1 text-sm bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-4 py-1.5 font-medium"
                    >
                        {currentDisplay ? "Change" : "Add"}
                    </button>
                </div>
            ) : (
                <div className="space-y-2">
                    <input
                        type={isSecret ? "password" : "text"}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        placeholder={placeholder}
                        autoComplete="off"
                        data-testid={`${testidPrefix}-input`}
                        className="w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]"
                    />
                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={() => { setEditing(false); setValue(""); }}
                            data-testid={`${testidPrefix}-cancel-btn`}
                            className="text-sm text-[#4A5568] hover:text-[#1C252E] inline-flex items-center gap-1"
                        >
                            <X className="w-4 h-4" strokeWidth={2} /> Cancel
                        </button>
                        <button
                            type="button"
                            onClick={submit}
                            disabled={busy}
                            data-testid={`${testidPrefix}-save-btn`}
                            className="inline-flex items-center gap-1 bg-[#E53935] hover:bg-[#D32F2F] disabled:opacity-60 text-white rounded-full px-4 py-1.5 text-sm font-medium"
                        >
                            <Save className="w-4 h-4" strokeWidth={2} /> {busy ? "Saving…" : "Save"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
